import { Button, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { memo } from 'react';
import { StakeErrors } from '../../@types';
import { useBetslipContext } from '../../contexts/BetslipContext';
import { hasUpdateStakesAction } from '../../helpers';
import { OddsChangeStatus } from '../../helpers/betslipMessageParser';
import { useUserData } from '../../queries';
import useLocalization from '../../hooks/useLocalization';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import { FormattedBalance } from '../atoms/FormattedBalance';
import { BETSLIP_PLACE_BET_BUTTON_SKIN_STYLES } from '../../styles/components/betslipPlaceBetButton';

type Props = {
  stakeErrors: StakeErrors;
  totalStakeAmount: number;
  updateStakes: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isDisabled: boolean;
  disabledSystems: string[];
};

const BetslipPlaceBetButton = ({ stakeErrors, totalStakeAmount, updateStakes, isDisabled, disabledSystems }: Props) => {
  const { data: userSettings } = useUserData();
  const { bets, errors, clearErrors } = useBetslipContext();
  const { t } = useLocalization();
  const styles = useSkinStyles(BETSLIP_PLACE_BET_BUTTON_SKIN_STYLES);

  const oddsChanges = userSettings?.sportsbookConfig?.oddsChanges ?? 'none';

  const acceptChanges = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    clearErrors();
  };

  const hasOnlySystemErrors = Object.keys(stakeErrors).length === disabledSystems.length;
  const showUpdateStakesButton = hasUpdateStakesAction(stakeErrors);

  if (!isEmpty(stakeErrors) && showUpdateStakesButton) {
    return (
      <Button
        disableRipple
        fullWidth
        sx={styles.updateStakeButton}
        onClick={updateStakes}
        disabled={hasOnlySystemErrors}
      >
        <Stack direction='column'>
          <Typography variant='h5'>{t('updateStakes')}</Typography>
        </Stack>
      </Button>
    );
  }

  const hasDecreasedOdds = bets.some((bet) => bet.oddsChange === OddsChangeStatus.decreased);
  if (!!errors.length && (oddsChanges === 'none' || (hasDecreasedOdds && oddsChanges === 'rising'))) {
    return (
      <Button disableRipple fullWidth sx={styles.acceptChangesButton} onClick={acceptChanges}>
        <Typography variant='h5'>{t('acceptChanges')}</Typography>
      </Button>
    );
  }

  if ((!!errors.length && oddsChanges === 'all') || (oddsChanges === 'rising' && !hasDecreasedOdds)) {
    return (
      <Button disableRipple fullWidth type='submit' sx={styles.placeBetButton} disabled={isDisabled}>
        <Typography variant='h5'>
          {t('placeBet')} <FormattedBalance balance={totalStakeAmount} variant='h5' sx={styles.placeBetAmount} />
        </Typography>
      </Button>
    );
  }

  return (
    <Button disableRipple fullWidth type='submit' sx={styles.placeBetButton} disabled={isDisabled}>
      <Typography variant='h5'>
        {t('placeBet')} <FormattedBalance balance={totalStakeAmount} variant='h5' sx={styles.placeBetAmount} />
      </Typography>
    </Button>
  );
};

export default memo(BetslipPlaceBetButton);
